import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, money, dateFmt, errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import {
  MagnifyingGlass, MapPin, Star, Buildings, Tag, Sparkle, CheckCircle, Clock, PaperPlaneTilt,
  ListBullets, MapTrifold, LinkSimple,
} from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import HotelMap from "@/components/HotelMap";

// module-level cache so the search survives tab switches
let lastSearch = null;

const RADII = [5, 10, 15, 25, 50];
const TYPES = [
  { v: "all", label: "All types" },
  { v: "hotel", label: "Hotels" },
  { v: "motel", label: "Motels" },
  { v: "guest_house", label: "Guest houses" },
  { v: "apartment", label: "Apartments / extended stay" },
  { v: "hostel", label: "Hostels" },
];
const STARS = [
  { v: 0, label: "Any rating" },
  { v: 2, label: "2★ & up" },
  { v: 3, label: "3★ & up" },
  { v: 4, label: "4★ & up" },
];

function nightsBetween(a, b) {
  try {
    const d = (new Date(b) - new Date(a)) / 86400000;
    return d > 0 ? Math.round(d) : 0;
  } catch {
    return 0;
  }
}

function RateRequestDialog({ hotel }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    check_in: "", check_out: "", num_rooms: 1, nightly_price: "", notes: "",
    hotel_email: hotel.email || "",
  });
  const F = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const nights = nightsBetween(form.check_in, form.check_out);
  const roomNights = nights * Number(form.num_rooms || 0);

  const mut = useMutation({
    mutationFn: (body) => api.createRateRequest(body),
    onSuccess: (res) => {
      qc.invalidateQueries();
      if (res.routed === "platform") {
        toast.success(`Request sent to ${hotel.name}. Track it under “My RFPs”.`);
      } else if (res.email_sent) {
        toast.success(`Invite emailed to ${hotel.name} — we'll track when they open it.`);
      } else {
        toast.success(`Request saved — copy the invite link to reach ${hotel.name}.`);
      }
      setOpen(false);
      setForm({ check_in: "", check_out: "", num_rooms: 1, nightly_price: "", notes: "", hotel_email: hotel.email || "" });
    },
    onError: (e) => toast.error(errMsg(e, "Could not send request")),
  });

  const submit = () => {
    if (!form.check_in || !form.check_out) return toast.error("Choose check-in and check-out dates");
    if (nights <= 0) return toast.error("Check-out must be after check-in");
    if (!form.nightly_price || Number(form.nightly_price) <= 0) return toast.error("Enter your target nightly price");
    mut.mutate({
      hotel_id: hotel.hotel_id || "",
      hotel_name: hotel.name,
      address: hotel.address || "",
      city: hotel.city || "",
      state: hotel.state || "",
      phone: hotel.phone || "",
      website: hotel.website || "",
      hotel_email: form.hotel_email || "",
      invite_base: window.location.origin,
      lat: hotel.lat ?? null,
      lng: hotel.lng ?? null,
      stars: hotel.stars ?? null,
      type: hotel.type || "hotel",
      check_in: form.check_in,
      check_out: form.check_out,
      num_rooms: Number(form.num_rooms) || 1,
      nightly_price: Number(form.nightly_price),
      room_nights: roomNights,
      notes: form.notes,
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`name-price-btn-${hotel.hotel_id || hotel.name.replace(/\s+/g, "-").toLowerCase()}`}
          className="bg-[#002FA7] text-white px-4 py-2 text-sm font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] flex items-center gap-2 whitespace-nowrap">
          <Tag size={16} weight="bold" /> Name your price
        </button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#111827] max-w-lg">
        <DialogHeader><DialogTitle className="font-display text-2xl">Name your price</DialogTitle></DialogHeader>
        <div className="-mt-2">
          <div className="font-display font-bold text-lg text-[#111827]">{hotel.name}</div>
          <div className="text-sm text-[#4B5563] flex items-center gap-2">
            <MapPin size={14} /> {hotel.address || `${hotel.city || ""} ${hotel.state || ""}`.trim() || "—"}
          </div>
          {!hotel.on_platform && (
            <div className="mt-2 text-xs bg-[#FEF3C7] text-[#92400E] px-2 py-1 inline-flex items-center gap-1 font-semibold">
              <Sparkle size={12} weight="fill" /> Not yet on RateBridge — we'll invite them
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Check-in</span>
            <input data-testid="rr-checkin" type="date" value={form.check_in} onChange={F("check_in")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Check-out</span>
            <input data-testid="rr-checkout" type="date" value={form.check_out} onChange={F("check_out")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Rooms</span>
            <input data-testid="rr-rooms" type="number" min="1" value={form.num_rooms} onChange={F("num_rooms")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Your price / night</span>
            <input data-testid="rr-price" type="number" min="1" placeholder="e.g. 89" value={form.nightly_price} onChange={F("nightly_price")}
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        </div>
        <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Notes (optional)</span>
          <textarea data-testid="rr-notes" value={form.notes} onChange={F("notes")} rows={3}
            placeholder="Crew size, parking, breakfast, flexible dates…"
            className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" /></label>
        {!hotel.on_platform && (
          <label className="text-sm"><span className="overline text-[#4B5563] block mb-1">Hotel email (optional — we'll email them the invite)</span>
            <input data-testid="rr-hotel-email" type="email" value={form.hotel_email} onChange={F("hotel_email")}
              placeholder="frontdesk@hotel.com"
              className="w-full border border-[#E5E7EB] px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
            <span className="text-xs text-[#9CA3AF] block mt-1">If left blank, you'll get a copy-able invite link instead.</span>
          </label>
        )}
        {roomNights > 0 && (
          <p className="text-sm text-[#4B5563]">
            {nights} nights × {form.num_rooms} rooms = <span className="font-mono font-semibold text-[#111827]">{roomNights} room-nights</span>
            {form.nightly_price ? <> · est. <span className="font-mono font-semibold text-[#111827]">{money(roomNights * Number(form.nightly_price))}</span></> : null}
          </p>
        )}
        <DialogFooter>
          <button data-testid="rr-submit" disabled={mut.isPending} onClick={submit}
            className="bg-[#002FA7] text-white px-6 py-3 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center gap-2">
            <PaperPlaneTilt size={16} weight="bold" /> {mut.isPending ? "Sending…" : "Send rate request"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function HotelCard({ hotel }) {
  return (
    <div className="rb-card rb-card-hover p-5 flex items-start justify-between gap-4"
      data-testid={`discover-hotel-${hotel.hotel_id || hotel.name.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display font-bold text-lg text-[#111827]">{hotel.name}</div>
          {hotel.on_platform ? (
            <span className="text-xs bg-[#DCFCE7] text-[#047857] px-2 py-0.5 font-semibold inline-flex items-center gap-1">
              <CheckCircle size={12} weight="fill" /> On RateBridge
            </span>
          ) : (
            <span className="text-xs bg-[#FEF3C7] text-[#92400E] px-2 py-0.5 font-semibold inline-flex items-center gap-1">
              <Sparkle size={12} weight="fill" /> New
            </span>
          )}
        </div>
        <div className="text-sm text-[#4B5563] flex items-center gap-1 mt-1">
          <MapPin size={14} /> {hotel.address || `${hotel.city || ""} ${hotel.state || ""}`.trim() || "Address unavailable"}
        </div>
        <div className="text-xs text-[#4B5563] flex items-center gap-3 mt-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><Buildings size={13} /> {hotel.type || "hotel"}</span>
          {hotel.stars ? <span className="inline-flex items-center gap-1"><Star size={13} weight="fill" color="#D97706" /> {hotel.stars}★</span> : null}
          {hotel.brand ? <span>{hotel.brand}</span> : null}
          {typeof hotel.distance_miles === "number" ? <span>{hotel.distance_miles} mi away</span> : null}
          {hotel.on_platform && hotel.base_rate ? <span className="font-mono text-[#111827]">from {money(hotel.base_rate)}/nt</span> : null}
        </div>
      </div>
      <RateRequestDialog hotel={hotel} />
    </div>
  );
}

export default function CompanyDiscover() {
  const [location, setLocation] = useState(lastSearch?.location || "");
  const [radius, setRadius] = useState(lastSearch?.radius || 15);
  const [minStars, setMinStars] = useState(lastSearch?.min_stars || 0);
  const [type, setType] = useState(lastSearch?.type || "all");
  const [submitted, setSubmitted] = useState(lastSearch);
  const [view, setView] = useState("list");

  const { data: pending = [] } = useQuery({ queryKey: ["rateRequests"], queryFn: api.rateRequests });

  const search = useQuery({
    queryKey: ["searchArea", submitted],
    queryFn: () => api.searchArea(submitted),
    enabled: !!submitted,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

  React.useEffect(() => {
    if (search.isError) toast.error(errMsg(search.error, "Search failed"));
  }, [search.isError, search.error]);

  const run = () => {
    if (!location.trim()) return toast.error("Enter a city, state or ZIP code");
    const params = { location: location.trim(), radius, min_stars: minStars, type };
    lastSearch = params;
    setSubmitted(params);
  };

  const copyInvite = (id) => {
    const link = `${window.location.origin}/invite/${id}`;
    navigator.clipboard?.writeText(link)
      .then(() => toast.success("Invite link copied — send it to the hotel"))
      .catch(() => toast.error("Could not copy link"));
  };

  const hotels = search.data?.hotels || [];
  const center = search.data ? { lat: search.data.lat, lng: search.data.lng, location: submitted?.location } : null;

  return (
    <div className="mt-8 space-y-6" data-testid="discover-panel">
      <div className="border border-[#111827] p-5">
        <span className="overline text-[#002FA7]">Find hotels anywhere</span>
        <h2 className="font-display font-bold text-xl text-[#111827] mt-1">Search real hotels near your project</h2>
        <p className="text-sm text-[#4B5563] mt-1">Enter a city + state or a ZIP code. We'll show every hotel in range — on RateBridge or not — so you can name your price.</p>
        <div className="grid md:grid-cols-[1fr_auto_auto_auto_auto] gap-3 mt-4">
          <input data-testid="discover-location" value={location} onChange={(e) => setLocation(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && run()}
            placeholder="City, State or ZIP  (e.g. Tulsa, OK  or  74103)"
            className="border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
          <select data-testid="discover-radius" value={radius} onChange={(e) => setRadius(Number(e.target.value))}
            className="border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {RADII.map((r) => <option key={r} value={r}>{r} mi</option>)}
          </select>
          <select data-testid="discover-stars" value={minStars} onChange={(e) => setMinStars(Number(e.target.value))}
            className="border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {STARS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          <select data-testid="discover-type" value={type} onChange={(e) => setType(e.target.value)}
            className="border border-[#E5E7EB] px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
            {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
          </select>
          <button data-testid="discover-search-btn" onClick={run} disabled={search.isFetching}
            className="bg-[#002FA7] text-white px-6 py-2.5 font-semibold hover:bg-[#00227A] transition-colors active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2">
            <MagnifyingGlass size={18} weight="bold" /> {search.isFetching ? "Searching…" : "Search"}
          </button>
        </div>
      </div>

      {search.isFetching && (
        <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563]">
          Searching real hotels near {submitted?.location}…
        </div>
      )}

      {!search.isFetching && submitted && !search.isError && (
        <div data-testid="discover-results">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
            <div className="text-sm text-[#4B5563]">
              {hotels.length} hotel{hotels.length === 1 ? "" : "s"} within {submitted.radius} mi of <span className="font-semibold text-[#111827]">{submitted.location}</span>
            </div>
            <div className="flex border border-[#E5E7EB]">
              <button data-testid="discover-view-list" onClick={() => setView("list")}
                className={`px-3 py-1.5 text-sm font-semibold flex items-center gap-1 transition-colors ${view === "list" ? "bg-[#002FA7] text-white" : "text-[#4B5563] hover:text-[#111827]"}`}>
                <ListBullets size={16} weight="bold" /> List
              </button>
              <button data-testid="discover-view-map" onClick={() => setView("map")}
                className={`px-3 py-1.5 text-sm font-semibold flex items-center gap-1 transition-colors ${view === "map" ? "bg-[#002FA7] text-white" : "text-[#4B5563] hover:text-[#111827]"}`}>
                <MapTrifold size={16} weight="bold" /> Map
              </button>
            </div>
          </div>

          {hotels.length === 0 ? (
            <div className="border border-dashed border-[#E5E7EB] py-16 text-center text-[#4B5563]">
              No hotels matched. Try a wider radius or a nearby city.
            </div>
          ) : view === "map" ? (
            <HotelMap center={center} radiusMiles={submitted.radius} hotels={hotels} />
          ) : (
            <div className="space-y-3">
              {hotels.map((h, i) => <HotelCard key={(h.hotel_id || h.name) + i} hotel={h} />)}
            </div>
          )}
        </div>
      )}

      {pending.length > 0 && (
        <div data-testid="pending-requests">
          <h3 className="font-display font-bold text-lg text-[#111827] flex items-center gap-2">
            <Clock size={18} weight="duotone" color="#D97706" /> Pending invites
          </h3>
          <p className="text-sm text-[#4B5563] mb-3">Rate requests sent to hotels not yet on RateBridge. Copy the invite link to send it to them directly.</p>
          <div className="space-y-3">
            {pending.map((p) => {
              const track = p.status === "claimed"
                ? { label: "Responded · joined", cls: "bg-[#DCFCE7] text-[#047857]" }
                : p.opened_at
                  ? { label: `Opened ${dateFmt(p.opened_at)}${p.views ? ` · ${p.views} views` : ""}`, cls: "bg-[#DBEAFE] text-[#1E40AF]" }
                  : p.email_sent
                    ? { label: "Emailed · not opened yet", cls: "bg-[#FEF3C7] text-[#92400E]" }
                    : { label: "Not opened yet", cls: "bg-[#F3F4F6] text-[#4B5563]" };
              return (
                <div key={p.id} className="rb-card p-5 flex items-center justify-between gap-4" data-testid={`pending-request-${p.id}`}>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[#111827]">{p.hotel_name}</div>
                    <div className="text-sm text-[#4B5563]">{p.city}{p.state ? `, ${p.state}` : ""} · {p.num_rooms} rooms · {dateFmt(p.check_in)}–{dateFmt(p.check_out)}</div>
                    <span className={`inline-block mt-2 text-xs px-2 py-0.5 font-semibold ${track.cls}`} data-testid={`pending-status-${p.id}`}>{track.label}</span>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <div className="font-mono font-semibold text-[#111827]">{money(p.nightly_price)}/nt</div>
                    </div>
                    <button data-testid={`copy-invite-${p.id}`} onClick={() => copyInvite(p.id)}
                      className="border border-[#111827] px-3 py-2 text-sm font-semibold hover:bg-[#111827] hover:text-white transition-colors flex items-center gap-1 whitespace-nowrap">
                      <LinkSimple size={15} weight="bold" /> Copy invite
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
